import { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import {
  GetCoursesByFacultyQuery,
  UnifiedSubmission,
  UpdateAssessmentSubmissionUpdatedGrade,
  UpdateStudentAssignmentGradeInput,
  useGetCoursesByFacultyQuery,
  useGetGradingCenterDashboardDataQuery,
  useGetUnifiedSubmissionsByFacultyQuery,
} from "@axio/graphql/generated/graphql";
import usePagination from "../hooks/usePagination";
import { useAppConfig } from "../AppConfigProvider";
import { useAuth } from "../hooks/useAuth";
import { useDebounce } from "use-debounce";

export const SUBMISSION_TYPE_OPTIONS = [
  { label: "All Types", value: "All" },
  { label: "Assignment", value: "Assignment" },
  { label: "Assessment", value: "Assessment" },
  { label: "Project", value: "Project" },
];

type AssessmentRetakePayload = {
  retakeAllowed: boolean;
  retakeDeadline: Date | null;
};

interface GradingCenterSubmissionsContextType {
  selectedCourse: string;
  setSelectedCourse: Dispatch<SetStateAction<string>>;
  selectedModule: string;
  setSelectedModule: Dispatch<SetStateAction<string>>;
  selectedType: (typeof SUBMISSION_TYPE_OPTIONS)[number]["value"];
  setSelectedType: Dispatch<SetStateAction<(typeof SUBMISSION_TYPE_OPTIONS)[number]["value"]>>;
  showOnlyUngraded: boolean;
  setShowOnlyUngraded: Dispatch<SetStateAction<boolean>>;
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
  sortBy: string;
  selectedCourses: string[];
  setSelectedCourses: Dispatch<SetStateAction<string[]>>;
  selectedModules: string[];
  setSelectedModules: Dispatch<SetStateAction<string[]>>;
  dashboardMetrics: {
    assignmentsToGrade: number;
    retakeRequests: number;
    newSubmissionsToday: number;
  };
  courseData: GetCoursesByFacultyQuery;
  filteredSubmissions: UnifiedSubmission[];
  totalSubmissionsCount: number;
  totalPages: number;
  refetchUnifiedSubmissionsData: () => void;
  selectedSubmissionData: UnifiedSubmission | null;
  setSelectedSubmissionData: Dispatch<SetStateAction<UnifiedSubmission | null>>;
  updatedAssessmentGradeEntry: UpdateAssessmentSubmissionUpdatedGrade | null;
  setUpdatedAssessmentGradeEntry: Dispatch<SetStateAction<UpdateAssessmentSubmissionUpdatedGrade | null>>;
  updatedAssessmentRetakePayload: AssessmentRetakePayload | null;
  setUpdatedAssessmentRetakePayload: Dispatch<SetStateAction<AssessmentRetakePayload | null>>;
  updatedAssignmentPayload: UpdateStudentAssignmentGradeInput | null;
  setUpdatedAssignmentPayload: Dispatch<SetStateAction<UpdateStudentAssignmentGradeInput | null>>;
  updatedProjectPayload: { grade: number | null; feedback: string } | null;
  setUpdatedProjectPayload: Dispatch<SetStateAction<{ grade: number | null; feedback: string } | null>>;
  canSaveAssignment: boolean;
  setCanSaveAssignment: Dispatch<SetStateAction<boolean>>;
  canSaveAssessment: boolean;
  setCanSaveAssessment: Dispatch<SetStateAction<boolean>>;
  canSaveProject: boolean;
  setCanSaveProject: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
}

const GradingCenterSubmissionsContext = createContext<GradingCenterSubmissionsContextType>({
  selectedCourse: "",
  setSelectedCourse: () => {},
  selectedModule: "",
  setSelectedModule: () => {},
  selectedType: "All",
  setSelectedType: () => {},
  showOnlyUngraded: false,
  setShowOnlyUngraded: () => {},
  searchQuery: "",
  setSearchQuery: () => {},
  page: 1,
  setPage: () => {},
  pageSize: 10,
  setPageSize: () => {},
  sortBy: "date_desc",
  selectedCourses: [],
  setSelectedCourses: () => {},
  selectedModules: [],
  setSelectedModules: () => {},
  dashboardMetrics: {
    assignmentsToGrade: 0,
    retakeRequests: 0,
    newSubmissionsToday: 0,
  },
  courseData: {} as GetCoursesByFacultyQuery,
  filteredSubmissions: [],
  totalSubmissionsCount: 0,
  totalPages: 0,
  refetchUnifiedSubmissionsData: () => {},
  selectedSubmissionData: null,
  setSelectedSubmissionData: () => {},
  updatedAssessmentGradeEntry: null,
  setUpdatedAssessmentGradeEntry: () => {},
  updatedAssessmentRetakePayload: null,
  setUpdatedAssessmentRetakePayload: () => {},
  updatedAssignmentPayload: null,
  setUpdatedAssignmentPayload: () => {},
  updatedProjectPayload: null,
  setUpdatedProjectPayload: () => {},
  canSaveAssignment: false,
  setCanSaveAssignment: () => {},
  canSaveAssessment: false,
  setCanSaveAssessment: () => {},
  canSaveProject: false,
  setCanSaveProject: () => {},
  isLoading: false,
});

const DEFAULT_PAGE_SIZE = 10;

export const GradingCenterSubmissionsProvider = ({ children }) => {
  const [selectedSubmissionData, setSelectedSubmissionData] = useState<UnifiedSubmission | null>(null);

  const [filteredSubmissions, setFilteredSubmissions] = useState<UnifiedSubmission[]>([]);

  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [showOnlyUngraded, setShowOnlyUngraded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  const [updatedAssessmentGradeEntry, setUpdatedAssessmentGradeEntry] =
    useState<UpdateAssessmentSubmissionUpdatedGrade | null>(null);
  const [updatedAssessmentRetakePayload, setUpdatedAssessmentRetakePayload] = useState<AssessmentRetakePayload | null>(
    null
  );
  const [updatedAssignmentPayload, setUpdatedAssignmentPayload] = useState<UpdateStudentAssignmentGradeInput | null>(
    null
  );
  const [updatedProjectPayload, setUpdatedProjectPayload] = useState<{ grade: number | null; feedback: string } | null>(
    null
  );
  const [canSaveAssignment, setCanSaveAssignment] = useState(false);
  const [canSaveAssessment, setCanSaveAssessment] = useState(false);
  const [canSaveProject, setCanSaveProject] = useState(false);

  const { userInfo } = useAuth();

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: DEFAULT_PAGE_SIZE });

  const sortBy = "date_desc";

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, selectedCourse, selectedModule, selectedType, showOnlyUngraded]);

  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const { data: gradingCenterDashboardData } = useGetGradingCenterDashboardDataQuery({
    variables: {
      facultyId: userInfo.userId,
    },
    skip: !userInfo.userId,
  });

  const dashboardMetrics = useMemo(() => {
    const data = gradingCenterDashboardData?.getGradingCenterDashboardData?.data;
    return {
      assignmentsToGrade: data?.assignmentsAndAssessmentsToGrade || 0,
      retakeRequests: 0, // Not available in current data
      newSubmissionsToday: data?.newSubmissionsToday || 0,
    };
  }, [gradingCenterDashboardData]);

  const { data: courseData } = useGetCoursesByFacultyQuery({
    variables: {
      schoolId,
      userId: userInfo.userId,
    },
    skip: !userInfo.userId,
  });

  useEffect(() => {
    if (courseData?.getCoursesByFaculty?.data) {
      const allCourseIds = courseData.getCoursesByFaculty.data.map((course) => course.id);
      const allModuleIds = courseData.getCoursesByFaculty.data
        .map((course) => course.modules.map((module) => module.id))
        .flat();

      setSelectedCourses(allCourseIds);
      setSelectedModules(allModuleIds);
    }
  }, [courseData]);

  const {
    data: unifiedSubmissionsData,
    refetch: refetchUnifiedSubmissionsData,
    loading: unifiedSubmissionsLoading,
  } = useGetUnifiedSubmissionsByFacultyQuery({
    variables: {
      input: {
        facultyId: userInfo.userId,
        searchQuery: debouncedSearchQuery,
        page,
        pageSize: pageSize,
        courseIds: selectedCourse ? [selectedCourse] : selectedCourses,
        moduleIds: selectedModule ? [selectedModule] : selectedModules,
        sortBy,
        submissionType: selectedType,
        showOnlyUngraded,
      },
    },
    skip: !userInfo.userId,
  });

  useEffect(() => {
    const submissionsAfterFilters = unifiedSubmissionsData?.getUnifiedSubmissionsByFaculty?.data?.submissions ?? [];
    setFilteredSubmissions(submissionsAfterFilters as UnifiedSubmission[]);
  }, [unifiedSubmissionsData]);

  useEffect(() => {
    refetchUnifiedSubmissionsData();
  }, [debouncedSearchQuery, page, pageSize, sortBy, selectedCourse, selectedModule, selectedType, showOnlyUngraded]);

  useEffect(() => {
    setFilteredSubmissions(
      (unifiedSubmissionsData?.getUnifiedSubmissionsByFaculty?.data?.submissions ?? []) as UnifiedSubmission[]
    );
  }, [unifiedSubmissionsData]);

  const totalSubmissionsCount = unifiedSubmissionsData?.getUnifiedSubmissionsByFaculty?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalSubmissionsCount / pageSize);

  const value = {
    selectedCourse,
    setSelectedCourse,
    selectedModule,
    setSelectedModule,
    selectedType,
    setSelectedType,
    showOnlyUngraded,
    setShowOnlyUngraded,
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortBy,
    selectedCourses,
    setSelectedCourses,
    selectedModules,
    setSelectedModules,
    dashboardMetrics,
    courseData,
    filteredSubmissions,
    totalSubmissionsCount,
    totalPages,
    refetchUnifiedSubmissionsData,
    selectedSubmissionData,
    setSelectedSubmissionData,
    updatedAssessmentGradeEntry,
    setUpdatedAssessmentGradeEntry,
    updatedAssessmentRetakePayload,
    setUpdatedAssessmentRetakePayload,
    updatedAssignmentPayload,
    setUpdatedAssignmentPayload,
    updatedProjectPayload,
    setUpdatedProjectPayload,
    canSaveAssignment,
    setCanSaveAssignment,
    canSaveAssessment,
    setCanSaveAssessment,
    canSaveProject,
    setCanSaveProject,
    isLoading: unifiedSubmissionsLoading,
  };

  return <GradingCenterSubmissionsContext.Provider value={value}>{children}</GradingCenterSubmissionsContext.Provider>;
};

// make this a context
export const useGradingCenterSubmissions = () => {
  const context = useContext(GradingCenterSubmissionsContext);
  if (!context) {
    throw new Error("useGradingCenterSubmissions must be used within a GradingCenterSubmissionsProvider");
  }
  return context;
};
